import { IStairCalculationResult, IStairConfig } from '@stair-craft/common/util';

import { horizontalDim, verticalDim } from './dim-builders';
import {
    EColorRef,
    EPrimitiveKind,
    ETextAlign,
    ETextBaseline,
    FONT_MONO_FAMILY,
    IFont,
    IPaneRect,
    IPlanPrimitive,
    IPoint,
    IPolyPrimitive,
    IProjectionOutput,
    ITextPrimitive,
    emptyProjection,
} from './plan-primitive';
import { ITextMeasurer } from './text-measurer';

const PAD_LEFT: number = 70;
const PAD_RIGHT: number = 50;
const PAD_TOP: number = 26;
const PAD_BOTTOM: number = 38;
const STRINGER_THICKNESS: number = 100;

const HYP_FONT: IFont = { weight: 400, size: 9, family: FONT_MONO_FAMILY };

// Проекторы объявлены свойствами-функциями, а не методами: их разбирают
// деструктуризацией (`const { wx, wy } = projector`), и отвязанный от объекта
// метод потерял бы `this`.
interface IProjector {
    readonly wx: (x: number) => number;
    readonly wy: (y: number) => number;
    readonly ws: (s: number) => number;
}

function fillStrokeRect(x: number, y: number, w: number, h: number, fill: EColorRef, stroke: EColorRef, width: number): IPolyPrimitive {
    return {
        kind: EPrimitiveKind.Poly,
        points: [
            [x, y],
            [x + w, y],
            [x + w, y + h],
            [x, y + h],
        ],
        closed: true,
        fill,
        stroke,
        width,
    };
}

/**
 * Step detail (чертёж ступеней) — two stacked treads riding a saw-cut stringer,
 * with the rise/going/nose/thickness dimensions and the hypotenuse label.
 * Pure projection in pane-local pixels; the drawer translates to the pane origin.
 * 1:1 port of the former PlanViewComponent#drawStepDetailStraight.
 */
export function projectStepDetail(
    result: IStairCalculationResult,
    config: IStairConfig,
    pane: IPaneRect,
    measurer: ITextMeasurer
): IProjectionOutput {
    const h: number = result.riserHeight;
    const b: number = result.treadDepth;
    const nose: number = config.noseLength;
    const treadT: number = config.treadThickness;
    const angleRad: number = (result.stairAngle * Math.PI) / 180;
    const stepHyp: number = Math.sqrt(b * b + h * h);
    const riserVisibleH: number = Math.max(0, h - treadT);

    if (h <= 0 || b <= 0) {
        return emptyProjection();
    }

    const innerW: number = pane.w - PAD_LEFT - PAD_RIGHT;
    const innerH: number = pane.h - PAD_TOP - PAD_BOTTOM;

    // World extent: 2 stacked steps. World x covers [-nose, 2b]; world y covers [0, 2h + treadT].
    const worldW: number = 2 * b + nose + 40;
    const worldH: number = 2 * h + treadT + 30;
    const scale: number = Math.min(innerW / worldW, innerH / worldH);

    const drawW: number = worldW * scale;
    const drawH: number = worldH * scale;
    // Pane-local: the drawer translates to the pane origin.
    const ox: number = PAD_LEFT + (innerW - drawW) / 2 + nose * scale;
    const oy: number = PAD_TOP + (innerH - drawH) / 2 + drawH;

    const projector: IProjector = {
        wx: (x: number): number => ox + x * scale,
        wy: (y: number): number => oy - y * scale,
        ws: (s: number): number => s * scale,
    };
    const { wx, wy, ws }: IProjector = projector;

    const primitives: IPlanPrimitive[] = [];

    // Saw-cut stringer body underneath both steps (a parallelogram with a sawtooth top edge).
    const sDx: number = STRINGER_THICKNESS * Math.sin(angleRad);
    const sDy: number = STRINGER_THICKNESS * Math.cos(angleRad);
    const stringerPoints: IPoint[] = [
        [wx(0), wy(0)],
        [wx(0), wy(h)],
        [wx(b), wy(h)],
        [wx(b), wy(2 * h)],
        [wx(2 * b), wy(2 * h)],
        [wx(2 * b + sDx), wy(2 * h - sDy)],
        [wx(sDx), wy(-sDy)],
    ];
    primitives.push({
        kind: EPrimitiveKind.Poly,
        points: stringerPoints,
        closed: true,
        fill: EColorRef.Paper2,
        stroke: EColorRef.Ink2,
        width: 1,
    });

    // Tread boards (with nose) — drawn above the stringer.
    primitives.push(fillStrokeRect(wx(-nose), wy(h), ws(b + nose), ws(treadT), EColorRef.Step, EColorRef.StepStroke, 1));
    primitives.push(fillStrokeRect(wx(b - nose), wy(2 * h), ws(b + nose), ws(treadT), EColorRef.Step, EColorRef.StepStroke, 1));

    // Floor line.
    primitives.push({
        kind: EPrimitiveKind.Poly,
        points: [
            [wx(-nose - 30), wy(0)],
            [wx(2 * b + 20), wy(0)],
        ],
        closed: false,
        stroke: EColorRef.Ink,
        width: 1.2,
    });

    // Dimension labels (matches zhitov m1/m3 layout).
    primitives.push(horizontalDim(wx(b - nose), wy(2 * h) + ws(treadT) + 18, wx(2 * b), String(Math.round(b + nose)), measurer));
    primitives.push(horizontalDim(wx(b - nose), wy(2 * h) - 12, wx(b), String(nose), measurer));
    primitives.push(verticalDim(wx(2 * b) + 14, wy(2 * h - treadT), wy(2 * h), String(treadT), measurer));
    primitives.push(verticalDim(wx(b) + 14, wy(h), wy(2 * h), String(Math.round(h)), measurer));
    primitives.push(verticalDim(wx(b) - 14, wy(h), wy(h + riserVisibleH), String(Math.round(riserVisibleH)), measurer));

    // Step diagonal (hypotenuse) — dashed line plus a rotated label between the steps.
    const diagX1: number = wx(0);
    const diagY1: number = wy(h);
    const diagX2: number = wx(b);
    const diagY2: number = wy(2 * h);
    primitives.push({
        kind: EPrimitiveKind.Poly,
        points: [
            [diagX1, diagY1],
            [diagX2, diagY2],
        ],
        closed: false,
        stroke: EColorRef.Ink3,
        width: 0.6,
        dash: [3, 3],
    });
    const hypLabel: ITextPrimitive = {
        kind: EPrimitiveKind.Text,
        at: [(diagX1 + diagX2) / 2 - 8, (diagY1 + diagY2) / 2],
        text: String(Math.round(stepHyp)),
        align: ETextAlign.Center,
        baseline: ETextBaseline.Middle,
        color: EColorRef.Ink3,
        font: HYP_FONT,
        rotation: -angleRad,
    };
    primitives.push(hypLabel);

    // Nose overhang under the lower tread.
    if (nose > 0) {
        primitives.push(horizontalDim(wx(-nose), wy(h) + 18, wx(0), String(nose), measurer));
    }
    // Single step span on the floor (b, projected without nose).
    primitives.push(horizontalDim(wx(0), wy(0) + 18, wx(b), String(Math.round(b)), measurer));

    // Millimetre origin: the lower tread's nosing line at the walking surface (world x = 0, y = 0).
    return { primitives, pxPerMm: scale, origin: { x: ox, y: oy } };
}
